import { useState } from 'react';
import { Trans, t } from '@lingui/macro';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';

import StyledDivider from 'components/Divider';

import { useCurrentUserContext } from 'utils/User';

import { TabButton } from './components/TabButton';

import OemSegments from './OemSegments';
import Teams from './Teams';
import Fleets from './Fleets';
import Depots from './Depots';

type TabKey = 'oem-segments' | 'teams' | 'fleets' | 'depots';
type TabObject = Record<TabKey, { label: string; value: React.ComponentType }>;

function getTabObject(tabKey: TabKey) {
  const TabObjects: TabObject = {
    'oem-segments': { label: t`OEM Segments`, value: OemSegments },
    teams: { label: t`Teams`, value: Teams },
    fleets: { label: t`Fleets`, value: Fleets },
    depots: { label: t`Depots`, value: Depots },
  };

  return TabObjects[tabKey];
}

const ManagementPage = () => {
  const { isSuperUser, isOemManager, oemSegments } = useCurrentUserContext();

  const oemSegmentsLength = oemSegments?.length ?? 0;
  const hasMultipleOemSegments = isOemManager && oemSegmentsLength > 1;
  const showOemSegmentsTab = isSuperUser || hasMultipleOemSegments;

  const [selectedTab, setSelectedTab] = useState<TabKey>(
    showOemSegmentsTab ? 'oem-segments' : 'teams',
  );

  const handleTabChange = (tab: TabKey) => () => {
    setSelectedTab(tab);
  };

  const { label, value: SelectedTabComponent } = getTabObject(selectedTab);

  return (
    <Flex flexDirection="column" flex={1}>
      <H3 marginX={2} marginTop={1.5} marginBottom={0.5}>
        <Trans>Management</Trans> / {label}
      </H3>
      <StyledDivider direction="horizontal" />
      <Flex marginX={2} marginY={1} style={{ columnGap: 5 }}>
        {showOemSegmentsTab && (
          <TabButton
            text={t`OEM Segments`}
            onClick={handleTabChange('oem-segments')}
            active={selectedTab === 'oem-segments'}
          />
        )}
        <TabButton
          text={t`Teams`}
          onClick={handleTabChange('teams')}
          active={selectedTab === 'teams'}
        />
        <TabButton
          text={t`Fleets`}
          onClick={handleTabChange('fleets')}
          active={selectedTab === 'fleets'}
        />
        <TabButton
          text={t`Depots`}
          onClick={handleTabChange('depots')}
          active={selectedTab === 'depots'}
        />
      </Flex>
      <StyledDivider direction="horizontal" />
      <SelectedTabComponent />
    </Flex>
  );
};

export default ManagementPage;
